'use client';
import { AppLink as Link } from '@curio/client';
import type { Account } from '@curio/contracts';
import { reviewerProgress } from '@curio/domain';
import { TrustBadge } from '@curio/ui';
import { ReviewerBadge } from '../components/reviewer-badge';
import { ContributionStats } from './contribution-stats';

export type ProfileTab = 'overview' | 'saved' | 'preferences';

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');

export function ProfileHeader({
  account,
  tab,
  projectCount,
}: {
  account: Account;
  tab: ProfileTab;
  projectCount: number | undefined;
}) {
  const rank = reviewerProgress(account.reviewerStats.acceptedReviews).current;
  const tabs: [ProfileTab, string, string][] = [
    ['overview', '/builder', 'Overview'],
    ['saved', '/builder/saved', 'Saved'],
    ['preferences', '/builder/preferences', 'Preferences'],
  ];
  return (
    <>
      <header className="profile-header">
        {account.image ? (
          <img className="profile-avatar" src={account.image} alt="" />
        ) : (
          <span className="profile-avatar" aria-hidden="true">
            {initials(account.name) || '?'}
          </span>
        )}
        <div className="profile-identity">
          <h1>{account.name}</h1>
          <div className="profile-meta">
            {projectCount !== undefined && (
              <span>
                {projectCount} {projectCount === 1 ? 'project' : 'projects'}
              </span>
            )}
            <ReviewerBadge tier={rank?.key} />
            {account.verifiedOwner && <TrustBadge>Verified creator</TrustBadge>}
            {account.operator && (
              <Link href="/operator" className="caption">
                Admin console
              </Link>
            )}
          </div>
        </div>
        <ContributionStats account={account} />
      </header>
      <nav className="profile-tabs" aria-label="Profile sections">
        {tabs.map(([key, href, label]) => (
          <Link
            key={key}
            href={href}
            aria-current={tab === key ? 'page' : undefined}
          >
            {label}
          </Link>
        ))}
      </nav>
    </>
  );
}
